import React, { useEffect, useRef, useMemo, useState } from "react";
import * as d3 from "d3";
import Citation from "features/MarkdownContent/components/Citation";
import { Text } from "design_system";
import {
  preparePapersForTimeline,
  getDateRange,
  getEffectiveDate,
  getCitationStats,
  getCitationScale,
  addBufferToDateRange,
  shouldUseYearlyGranularity,
} from "../utils/timeline/timeline";
import { TimelineDataPoint, TimelinePlotProps } from "../utils/timeline/types";
import { ResultsTimelinePlaceholder } from "features/MarkdownContent/components/ResultsTimeline";

// Timeline display constants
const YEAR_GRANULARITY_CUTOFF = 7; // Use yearly ticks if span > 7 years, else monthly
const BASE_COLLISION_RADIUS = 16; // Base radius for collision detection
const BASE_CITATION_HEIGHT = 20; // Base height for citation component
const DESKTOP_MAX_TICKS = 12; // Maximum number of ticks on desktop
const MOBILE_MAX_TICKS = 4; // Maximum number of ticks on mobile
const MAX_SIMULATION_TICKS = 100; // Maximum number of ticks for simulation
const DESKTOP_MAX_WIDTH = 768; // Maximum width for desktop
const DEFAULT_HEIGHT = 250; // Default height
const BOTTOM_MARGIN = 24; // Bottom margin, 24px is the height of the x axis

const BeeswarmPaperPlot: React.FC<TimelinePlotProps> = ({
  papers = [],
  onClickCitation,
  searchId,
  showCitationMeterResult,
  width = DESKTOP_MAX_WIDTH,
  height = DEFAULT_HEIGHT,
  margin = { top: 0, right: 0, bottom: BOTTOM_MARGIN, left: 0 },
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tickValues, setTickValues] = useState<
    { value: Date; x: number; label: string }[]
  >([]);

  // Calculate citation statistics for sizing papers
  const citationStats = useMemo(() => getCitationStats(papers), [papers]);

  // Prepare timeline data with beeswarm positioning
  const timelineData = useMemo((): TimelineDataPoint[] => {
    if (!papers.length) return [];

    const validPapers = preparePapersForTimeline(papers);
    if (!validPapers.length) return [];

    // Get buffered date range for better visualization
    const dateRange = getDateRange(validPapers);
    const bufferedRange = addBufferToDateRange(
      dateRange,
      YEAR_GRANULARITY_CUTOFF
    );

    // Create time scale
    const xScale = d3
      .scaleTime()
      .domain([bufferedRange.min, bufferedRange.max])
      .range([margin.left, width - margin.right]);

    // Create simulation nodes with scaled collision radii based on citations
    const simulationNodes = validPapers.map((paper) => {
      const date = getEffectiveDate(paper);
      const citationCount = paper.citation_count || 0;
      const scale = getCitationScale(citationCount, citationStats);

      return {
        ...paper,
        x: xScale(date),
        y: height / 2 - margin.bottom / 2,
        vx: 0,
        vy: 0,
        fx: null,
        fy: null,
        originalIndex: papers.findIndex((p) => p.paper_id === paper.paper_id),
        publicationDate: date,
        collisionRadius: BASE_COLLISION_RADIUS * scale,
        scale,
      };
    });

    // Run D3 force simulation to position papers without overlap
    const simulation = d3
      .forceSimulation(simulationNodes)
      .force("x", d3.forceX((d: any) => xScale(d.publicationDate)).strength(1)) // Pull to publication date
      .force("y", d3.forceY(height / 2 - margin.bottom / 2).strength(0.3)) // Pull toward vertical center
      .force(
        "collide",
        d3.forceCollide((d: any) => d.collisionRadius)
      ) // Prevent overlap
      .stop();

    // Run simulation ticks
    for (let i = 0; i < MAX_SIMULATION_TICKS; ++i) {
      simulation.tick();
    }

    // Convert simulation results to TimelineDataPoint format
    return simulationNodes.map((node) => ({
      paper: node,
      x: node.x || xScale(node.publicationDate),
      y: node.y,
      originalIndex: node.originalIndex,
      publicationDate: node.publicationDate,
      scale: node.scale,
    }));
  }, [
    papers,
    width,
    height,
    margin.top,
    margin.right,
    margin.bottom,
    margin.left,
    citationStats,
  ]);

  // Setup D3 axis and grid
  useEffect(() => {
    if (!svgRef.current || !papers.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const validPapers = preparePapersForTimeline(papers);
    const dateRange = getDateRange(validPapers);
    const bufferedRange = addBufferToDateRange(
      dateRange,
      YEAR_GRANULARITY_CUTOFF
    );

    // Create time scale
    const xScale = d3
      .scaleTime()
      .domain([bufferedRange.min, bufferedRange.max])
      .range([margin.left, width - margin.right]);

    const domain = xScale.domain();
    const range = domain[1].getTime() - domain[0].getTime();
    const marginTime = range * 0.05; // 5% margin from each edge

    // Determine granularity (yearly vs monthly)
    const useYearly = shouldUseYearlyGranularity(
      validPapers,
      YEAR_GRANULARITY_CUTOFF
    );

    const isMobile = width < DESKTOP_MAX_WIDTH;
    const maxTicks = isMobile ? MOBILE_MAX_TICKS : DESKTOP_MAX_TICKS;

    // Generate and filter ticks to prevent edge overflow
    let ticks = xScale.ticks(maxTicks);

    if (!useYearly && ticks.length > maxTicks) {
      const interval = Math.ceil(ticks.length / maxTicks);
      ticks = ticks.filter((_, i) => i % interval === 0);
    }

    ticks = ticks.filter((tick) => {
      const tickTime = tick.getTime();
      return (
        tickTime >= domain[0].getTime() + marginTime &&
        tickTime <= domain[1].getTime() - marginTime
      );
    });

    // Create grid lines
    const xGrid = d3
      .axisBottom(xScale)
      .tickSize(-height + margin.top + margin.bottom)
      .tickFormat(() => "")
      .tickValues(ticks);

    svg
      .append("g")
      .attr("class", "x-grid")
      .attr("transform", `translate(0, ${height - margin.bottom})`)
      .call(xGrid)
      .call((g) =>
        g
          .selectAll(".tick line")
          .attr("stroke", "var(--cdl-border-base)")
          .attr("stroke-width", 1)
          .attr("stroke-dasharray", "12,12")
      )
      .call((g) => g.select(".domain").remove());

    // Create axis (without labels)
    const xAxis = d3.axisBottom(xScale).tickFormat(() => "");

    svg
      .append("g")
      .attr("class", "x-axis")
      .attr("transform", `translate(0, ${height - margin.bottom})`)
      .call(xAxis as any)
      .call((g) =>
        g.selectAll(".tick").attr("color", "var(--cdl-border-emphasis)")
      )
      .call((g) => g.select(".domain").remove());

    // Generate tick data for custom Text labels
    const tickData = ticks.map((tick) => ({
      value: tick,
      x: xScale(tick),
      label: useYearly
        ? d3.timeFormat("%Y")(tick)
        : d3.timeFormat("%b %Y")(tick),
    }));

    setTickValues(tickData);
  }, [
    papers,
    width,
    height,
    margin.top,
    margin.right,
    margin.bottom,
    margin.left,
  ]);

  // Empty state
  if (papers.length === 0) {
    return <ResultsTimelinePlaceholder />;
  }

  return (
    <div className="w-full">
      <div
        className="relative border-t border-border-emphasis"
        ref={containerRef}
      >
        {/* SVG layer for grid lines and axis */}
        <svg
          ref={svgRef}
          width={width}
          height={height}
          className="absolute top-0 left-0 pointer-events-none"
        />

        {/* Paper citations positioned via force simulation */}
        <div className="relative" style={{ width, height }}>
          {timelineData.map((dataPoint) => (
            <div
              key={dataPoint.paper.paper_id}
              className="absolute transition-all duration-250 ease-out flex items-center justify-center"
              style={{
                left:
                  dataPoint.x - (dataPoint.scale * BASE_COLLISION_RADIUS) / 2,
                top: dataPoint.y - (dataPoint.scale * BASE_CITATION_HEIGHT) / 2,
              }}
            >
              <Citation
                paper={dataPoint.paper}
                onClick={onClickCitation}
                paperIndex={dataPoint.originalIndex}
                searchId={searchId}
                showCitationMeterResult={showCitationMeterResult}
                lastInGroup
                scale={dataPoint.scale}
              />
            </div>
          ))}
        </div>

        {/* Custom tick labels */}
        <div
          className="absolute left-0 bottom-0 border-t border-border-emphasis pointer-events-none h-6"
          style={{ width }}
        >
          {tickValues.map((tick, index) => (
            <div
              key={index}
              className="absolute bottom-0"
              style={{
                left: tick.x,
                transform: "translateX(-50%)",
              }}
            >
              <Text size="xx-sm" theme="muted" className="m-0! h-full">
                {tick.label}
              </Text>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BeeswarmPaperPlot;
